import React, { useRef } from 'react'
import { gql, useQuery } from '@apollo/client'
import { useHistory, useParams } from 'react-router-dom'
import { Box, Typography } from '@mui/material'
import { FormFrame, useApplicationFrame } from '../components/PartnerSignups/ApplicationFrame'
import { Icon, T } from '../components/PartnerSignups/onboardingDesign'
import { Badge, OnboardingPage, PageHeader } from '../components/PartnerSignups/onboardingUi'

/**
 * An agent filling in a restaurant's application, in the agent portal: the
 * merchant sign-up form itself, framed with the agent's staff link (the same
 * form a super admin fills in). "new" starts one. Agents can open only the
 * applications they started, and only until the restaurant signs
 * (backend merchantStaffLink).
 */

const REFERRALS = gql`
  query AgentApplicationReferrals {
    myAgentReferrals { _id restaurantName contactEmail status pricing held requestNote canOpen }
  }
`

const PORTAL = '/agent/home'
const PAGE = '/agent/application'

const PRICING = {
  STANDARD: ['Standard fees', 'neutral'],
  CUSTOM: ['Own fees, approved', 'green'],
  PENDING: ['Fees waiting for a super admin', 'amber'],
  REJECTED: ['Fees not approved', 'red'],
}

export default function AgentApplication() {
  const { id } = useParams()
  const history = useHistory()
  const isNew = id === 'new'
  const createdHere = useRef(null)
  const { data, refetch } = useQuery(REFERRALS, { fetchPolicy: 'network-only' })
  const referral = ((data && data.myAgentReferrals) || []).find(r => String(r._id) === String(id))

  const frame = useApplicationFrame({
    id,
    created: createdHere,
    onSaved: () => { Promise.resolve().then(() => refetch()).catch(() => {}) },
    onCreated: (newId) => {
      history.replace(`${PAGE}/${newId}`)
      Promise.resolve().then(() => refetch()).catch(() => {})
    },
  })

  const pricing = referral && PRICING[referral.pricing]
  return (
    <OnboardingPage maxWidth={1240} sx={{ pb: 3 }}>
      <PageHeader
        eyebrow="Agent portal"
        back={{ label: 'Agent portal', onClick: () => history.push(PORTAL) }}
        title={isNew ? 'New application' : (referral && referral.restaurantName) || 'Application'}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1 }}>
          {referral && (referral.held
            ? <Badge kind="neutral">Not sent to the restaurant yet</Badge>
            : <Badge kind="info">{referral.contactEmail ? `Sent to ${referral.contactEmail}` : 'Sent to the restaurant'}</Badge>)}
          {pricing && <Badge kind={pricing[1]}>{pricing[0]}</Badge>}
          {referral && referral.requestNote && referral.pricing === 'REJECTED' && (
            <Typography sx={{ color: T.error, fontSize: 14 }}>Not approved: {referral.requestNote}</Typography>
          )}
        </Box>
      </PageHeader>

      <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', background: T.infoBg, border: `1px solid ${T.infoBorder}`, color: T.infoText, borderRadius: '16px', px: 2, py: 1.5, mb: 2 }}>
        <Box sx={{ display: 'flex', mt: '2px' }}><Icon.Info size={18} /></Box>
        <Typography sx={{ fontSize: 14, color: T.infoText, lineHeight: 1.55 }}>
          {isNew
            ? 'Fill in step 1 and create the application. Nothing goes to the restaurant until you send it on the last step.'
            : 'Fill in what you know; it saves as you go. Set this restaurant’s fees on Fees & terms: fees that differ from the standard need a super admin’s approval first. Then send it on the last step: the owner completes anything missing, accepts the terms and signs.'}
        </Typography>
      </Box>

      <FormFrame frame={frame} sx={{ height: { xs: '78vh', md: 'calc(100vh - 290px)' }, minHeight: 560 }} />
    </OnboardingPage>
  )
}
